import React, { useEffect, useRef, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { validateEntraEmail } from '../utils/emailValidator';

const AuthenticatedTemplate = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const loginAttemptedRef = useRef(false);
  const [accountTypeError, setAccountTypeError] = useState(null);

  useEffect(() => {
    // Check if user is authenticated with a valid account type
    if (isAuthenticated && accounts && accounts.length > 0) {
      const account = accounts[0];
      const userEmail = account.idTokenClaims?.email || 
                       account.idTokenClaims?.preferred_username || 
                       account.username;
      
      // Check if this is a consumer account (not Entra ID work/school)
      const emailValidation = validateEntraEmail(userEmail);
      
      if (!emailValidation.isValid) {
        // User is signed in with a consumer account - block access
        setAccountTypeError(
          `Access Denied: This portal is restricted to Microsoft work or school accounts (Azure AD/Entra ID). 
          You are currently signed in with a personal account (${userEmail}). 
          Please sign out and sign in with your organization's work account.`
        );
        return;
      }
      
      // Valid account - clear any previous errors
      setAccountTypeError(null);
    }
    
    // Automatically trigger login if user is not authenticated and no login is in progress
    // Use ref to prevent multiple login attempts
    if (!isAuthenticated && inProgress === 'none' && !loginAttemptedRef.current) {
      loginAttemptedRef.current = true;
      instance.loginRedirect(loginRequest).catch((error) => {
        loginAttemptedRef.current = false; // Reset on error to allow retry
      });
    }
  }, [isAuthenticated, accounts, inProgress, instance]);

  const handleLogout = () => {
    setAccountTypeError(null); // Clear error on logout
    instance.logoutRedirect().catch((error) => {
      // Logout error handled silently
    });
  };

  // Show account type error if user signed in with consumer account
  if (accountTypeError) {
    return (
      <div className="auth-container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem'
      }}>
        <div className="auth-card" style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          padding: '3rem',
          maxWidth: '600px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', color: '#dc3545' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="currentColor" viewBox="0 0 16 16" style={{ display: 'block', margin: '0 auto' }}>
              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
            </svg>
          </div>
          <h2 style={{ margin: '0 0 1.5rem 0', color: '#dc3545', fontSize: '1.8rem' }}>
            Access Denied
          </h2>
          <div style={{ 
            background: '#fff3cd', 
            border: '2px solid #ffc107',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '2rem',
            textAlign: 'left'
          }}>
            <p style={{ margin: '0', fontSize: '1rem', color: '#856404', lineHeight: '1.6' }}>
              {accountTypeError}
            </p>
          </div>
          <button 
            onClick={handleLogout}
            style={{
              padding: '0.75rem 2rem',
              background: '#dc3545',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#c82333'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#dc3545'}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while authentication is in progress
  if (inProgress === 'login') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Signing in...</h2>
          <p>Please wait while we sign you in.</p>
        </div>
      </div>
    );
  }

  // Show loading state while auto-redirecting to login
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Redirecting to Sign In...</h2>
          <p>You will be automatically redirected to Microsoft sign in.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {children}
    </div>
  );
};

export default AuthenticatedTemplate;